import React, { useContext, useEffect, useState } from "react";
import useProfileDashboard from "../../services/Settings/useProfileDashboard";
import useOrder from "../../services/Settings/useOrder";
import NoIndex from "../../components/NoIndex";
import { toast, ToastContainer } from "react-toastify";
import { GlobalContext } from "../../Context/GlobalContext";
import { RotateCcw } from "lucide-react";
import LoadingScreen from "../../utils/LoadingScreen";

const ProfileSetting = () => {
 const {
  fetchOrganizationDetails,
  updateOrganizationInfo,
  fetchReceiptDetails,
  getBankAccounts,
  addBankAccount,
  updateReceiptInfo,
} = useProfileDashboard();

  const { deliveryType, getDeliveryType, selectDeliveryPartner } = useOrder();
  const { userID } = useContext(GlobalContext);
  const [receiptDetails, setReceiptDetails] = useState(null);
  const [bankInfo, setBankInfo] = useState([]);
  const [showBankModal, setShowBankModal] = useState(false);

  const [accountNumber, setAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [bankName, setBankName] = useState("");
  const [holderName, setHolderName] = useState("");
  const [selectedDeliveryType, setSelectedDeliveryType] = useState("");
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deliveryTypeUpdating, setDeliveryTypeUpdating] = useState(false);

  const [address, setAddress] = useState("");
  const [phoneNo, setPhoneNo] = useState("");
  const [tagLine1, setTagLine1] = useState("");
  const [tagLine2, setTagLine2] = useState("");
  const [watermark1, setWatermark1] = useState("");
  const [watermark2, setWatermark2] = useState("");
  const [gst, setGst] = useState("");

  /* Fetch Data */

  const loadAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadReceipt(), loadBanks(), loadDeliveryType()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const loadReceipt = async () => {
    const receipt = await fetchReceiptDetails();
    if (receipt?.response?.length > 0) {
      setReceiptDetails(receipt.response[0]);
    }
  };

  const loadBanks = async () => {
    const banks = await getBankAccounts();
    if (banks?.response) {
      setBankInfo(banks.response);
    }
  };

  const loadDeliveryType = async () => {
    const res = await getDeliveryType();

    if (res?.response) {
      setSelectedDeliveryType(res.response.deliveryPartner);
    }
  };

  const handleDeliveryTypeChange = async (type) => {
    if (deliveryTypeUpdating) return;

    try {
      setDeliveryTypeUpdating(true);

      const res = await selectDeliveryPartner(type);

      if (res?.responseCode === "00") {
        setSelectedDeliveryType(type);
        toast.success("Packky Delivery Service selected successfully");
      } else {
        toast.error(
          res?.responseMessage || "Failed to update delivery service",
        );
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setDeliveryTypeUpdating(false);
    }
  };

  const handleAddBank = async () => {
    // validation
    if (!accountNumber) {
      toast.error("Account Number is required");
      return;
    }

    if (!ifscCode) {
      toast.error("IFSC Code is required");
      return;
    }

    if (!bankName) {
      toast.error("Bank Name is required");
      return;
    }

    if (!holderName) {
      toast.error("Account Holder Name is required");
      return;
    }

    const payload = {
      userid: userID,
      account: accountNumber,
      ifsc: ifscCode,
      bankName: bankName,
      bankHolderName: holderName,
    };

    const res = await addBankAccount(payload);

    if (res?.responseCode === "00") {
      setShowBankModal(false);

      setAccountNumber("");
      setIfscCode("");
      setBankName("");
      setHolderName("");

      loadBanks();
    }
  };

  const handleUpdateReceiptClick = () => {
    setAddress(receiptDetails?.address || "");
    setPhoneNo(receiptDetails?.phone_no || "");
    setTagLine1(receiptDetails?.tag_line1 || "");
    setTagLine2(receiptDetails?.tag_line2 || "");
    setWatermark1(receiptDetails?.watermark1 || "");
    setWatermark2(receiptDetails?.watermark2 || "");
    setGst(receiptDetails?.gst || "");

    setShowReceiptModal(true);
  };

  const handleSaveReceiptChanges = async () => {
    const payload = {
      address,
      phone_no: phoneNo,
      tag_line1: tagLine1,
      tag_line2: tagLine2,
      watermark1,
      watermark2,
      gst,
      userid: userID,
    };

    const res = await updateReceiptInfo(payload);

    if (res?.responseCode === "00") {
      setShowReceiptModal(false);

      loadReceipt(); // refresh receipt info
    }
  };

  return (
    <>
      <NoIndex />
<ToastContainer/>
      <div className="p-6 min-h-0 h-full flex flex-col overflow-hidden montserrat-font-1">
        <div className="sticky top-0 z-20 pb-4 flex justify-between items-center mb-6 montserrat-font-1">
          <h1 className="text-xl font-semibold">Settings</h1>

          <button
            onClick={loadAllData}
            className="border px-3 py-2 rounded-lg text-white text-sm bg-blue-500 items-center flex gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Refresh
          </button>
        </div>

 <div className="flex-1 overflow-y-auto min-h-0 pr-2">
        {loading && (
          <div className="flex justify-center py-6">
            <LoadingScreen text="Loading..." />
          </div>
        )}
        {/* Top Section */}
        <div className="grid grid-cols-12 gap-6 montserrat-font-1">
          {/* Delivery Service */}
          <div className="col-span-12 lg:col-span-8 overflow-auto h-full bg-white border rounded-lg p-6 montserrat-font-1">
            <h2 className="text-lg font-semibold">Delivery Service Type</h2>
            <p className="text-sm text-gray-500 mt-1 montserrat-font-2">
              Choose the delivery service that suits your needs:
            </p>

            <hr className="my-4" />

            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1 montserrat-font-2">
              <li>
                <b>Restaurant Delivery Service</b> - Perfect for restaurants
                with their own delivery staff, ensuring seamless order
                fulfillment.
              </li>
              <li>
                <b>Packky Delivery Service</b> - Utilize our platform's
                dedicated delivery partners for reliable and efficient order
                deliveries.
              </li>
              <li>
                <b>DoorDash Delivery Service</b> - Available exclusively in the
                USA, connect with a vast network of delivery professionals.
              </li>
            </ul>

            <hr className="my-6" />

            {/* Buttons */}
            <div className="flex items-center justify-between w-full montserrat-font-1">
              {/* Restaurant */}
              <button className="w-[190px] py-4 rounded-lg border bg-gray-100 text-gray-500 cursor-not-allowed text-center">
                Restaurant Delivery Service
              </button>

              {/* Packky */}
              <button
                onClick={() => handleDeliveryTypeChange("PACKKY")}
                disabled={deliveryTypeUpdating}
              className={`w-[150px] py-4 rounded-lg border text-center font-medium transition
              ${
                selectedDeliveryType === "PACKKY"
                  ? "bg-blue-100 border-blue-500 text-blue-600"
                  : "bg-white text-gray-700"
              }
              ${deliveryTypeUpdating ? "opacity-60 cursor-not-allowed" : ""}
              `}
              >
                {deliveryTypeUpdating
                  ? "Updating..."
                  : "Packky Delivery Service"}
              </button>

              {/* Doordash */}
              <button className="w-[180px] py-4 rounded-lg border bg-gray-100 text-gray-500 cursor-not-allowed text-center montserrat-font-1">
                DoorDash Delivery Service
              </button>
            </div>
          </div>

          {/* Bank Details */}
          <div className="col-span-12 lg:col-span-4 bg-white border rounded-lg p-6 montserrat-font-1">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Bank Details</h2>

              <button
                onClick={() => setShowBankModal(true)}
                className="border border-blue-500 text-blue-600 px-3 py-1 rounded hover:bg-blue-50"
              >
                Add Account
              </button>
            </div>

            <hr className="mb-4" />

            <table className="w-full text-sm border montserrat-font-1 bg-white">
              <thead className="bg-green-100 montserrat-font-1">
                <tr>
                  <th className="border p-2 text-left">Account Number</th>
                  <th className="border p-2 text-left">Bank Name</th>
                  <th className="border p-2 text-left">IDFC Code</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="3" className="py-10">
                      <div className="flex justify-center">
                        <LoadingScreen text="Loading..." />
                      </div>
                    </td>
                  </tr>
                ) : bankInfo?.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="text-center py-6 text-gray-400">
                      No Bank Data Found
                    </td>
                  </tr>
                ) : (
                  bankInfo.map((bank) => (
                    <tr key={bank.id}>
                      <td className="border p-2">{bank.us_account}</td>
                      <td className="border p-2">{bank.bank_name}</td>
                      <td className="border p-2">{bank.ifsc_code}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Receipt Section */}
        <div className="bg-white border rounded-lg p-6 mt-6 montserrat-font-1">
          <h2 className="text-lg font-semibold">Receipt Information</h2>
          <p className="text-sm text-gray-500 mt-1 montserrat-font-2">
            This information will appear on the customer's receipt.
          </p>

          <hr className="my-6" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 montserrat-font-1">
            <Input label="Address" value={receiptDetails?.address || ""} />
            <Input
              label="Phone Number"
              value={receiptDetails?.phone_no || ""}
            />
            <Input label="Tagline 1" value={receiptDetails?.tag_line1 || ""} />
            <Input label="Tagline 2" value={receiptDetails?.tag_line2 || ""} />
            <Input
              label="Watermark 1"
              value={receiptDetails?.watermark1 || ""}
            />
            <Input
              label="Watermark 2"
              value={receiptDetails?.watermark2 || ""}
            />
            <Input label="GST" value={receiptDetails?.gst || ""} />
          </div>

          <button
            onClick={handleUpdateReceiptClick}
            className="mt-6 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Update
          </button>
        </div>
         </div>
      </div>

      {/* Add Account */}

      {showBankModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 montserrat-font-1">
          <div className="bg-white w-[520px] rounded-lg shadow-lg montserrat-font-1">
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Update Bank Information</h2>

              <button
                onClick={() => setShowBankModal(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4 montserrat-font-1">
              <div>
                <label className="text-sm text-gray-600">Account Number</label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  className="w-full border rounded-md mt-1 p-2"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">IFSC Code</label>
                <input
                  type="text"
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value)}
                  className="w-full border rounded-md mt-1 p-2"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Bank Name</label>
                <input
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="w-full border rounded-md mt-1 p-2"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">
                  Account Holder Name
                </label>
                <input
                  type="text"
                  value={holderName}
                  onChange={(e) => setHolderName(e.target.value)}
                  className="w-full border rounded-md mt-1 p-2"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t montserrat-font-1">
              <button
                onClick={() => setShowBankModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded"
              >
                Cancel
              </button>

              <button
                onClick={handleAddBank}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* update reciept information */}

      {showReceiptModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 montserrat-font-1">
          <div className="bg-white w-[520px] rounded-lg shadow-lg montserrat-font-1">
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b montserrat-font-1">
              <h2 className="text-lg font-semibold">
                Update Receipt Information
              </h2>

              <button
                onClick={() => setShowReceiptModal(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4 montserrat-font-1">
              <div>
                <label className="text-sm text-gray-600">Address</label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full border rounded-md mt-1 p-2"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Phone Number</label>
                <input
                  type="text"
                  value={phoneNo}
                  onChange={(e) => setPhoneNo(e.target.value)}
                  className="w-full border rounded-md mt-1 p-2"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Tagline 1</label>
                <input
                  type="text"
                  value={tagLine1}
                  disabled
                  className="w-full border rounded-md mt-1 p-2 bg-gray-100"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Tagline 2</label>
                <input
                  type="text"
                  value={tagLine2}
                  disabled
                  className="w-full border rounded-md mt-1 p-2 bg-gray-100"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Watermark 1</label>
                <input
                  type="text"
                  value={watermark1}
                  onChange={(e) => setWatermark1(e.target.value)}
                  className="w-full border rounded-md mt-1 p-2"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Watermark 2</label>
                <input
                  type="text"
                  value={watermark2}
                  onChange={(e) => setWatermark2(e.target.value)}
                  className="w-full border rounded-md mt-1 p-2"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">GST</label>
                <input
                  type="text"
                  value={gst}
                  onChange={(e) => setGst(e.target.value)}
                  className="w-full border rounded-md mt-1 p-2"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t montserrat-font-1">
              <button
                onClick={() => setShowReceiptModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded"
              >
                Cancel
              </button>

              <button
                onClick={handleSaveReceiptChanges}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
          
        </div>
      )}
    </>
  );
};

const Input = ({ label, value }) => (
  <div>
    <label className="text-sm text-gray-700">{label}</label>
    <input
      value={value || "N/A"}
      readOnly
      className="w-full mt-1 border rounded-md p-2 bg-gray-100"
    />
  </div>
 
);

export default ProfileSetting;
